import React, { useState, useEffect, useCallback, useRef } from 'react';
import settlementService from '../../services/settlementService';

export const DetailedTransactionsTab = ({
  user,
  centres = [],
  showToast,
  onOpenReceipt
}) => {
  const isAccountsOrAdmin =
    user?.roleName?.toUpperCase() === 'ADMINISTRATOR' ||
    user?.roleId === 1 ||
    user?.roleName?.toLowerCase().includes('account');

  // Filter States
  const [search, setSearch] = useState('');
  const [datePreset, setDatePreset] = useState('today');
  const [fromDate, setFromDate] = useState(() => new Date().toISOString().split('T')[0]);
  const [toDate, setToDate] = useState(() => new Date().toISOString().split('T')[0]);
  const [centreId, setCentreId] = useState('');
  const [employeeId, setEmployeeId] = useState('');
  const [paymentMode, setPaymentMode] = useState('all');
  const [transactionStatus, setTransactionStatus] = useState('all');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [sortBy, setSortBy] = useState('Date');
  const [sortDirection, setSortDirection] = useState('DESC');

  // Data States
  const [loading, setLoading] = useState(false);
  const [transactions, setTransactions] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [receptionists, setReceptionists] = useState([]);

  // Load receptionists for filter
  useEffect(() => {
    if (isAccountsOrAdmin) {
      settlementService
        .getReceptionists({ centreId: centreId || undefined })
        .then((res) => {
          if (res.success && res.data) {
            setReceptionists(res.data);
          }
        })
        .catch(() => {});
    }
  }, [isAccountsOrAdmin, centreId]);

  // Handle Preset Changes
  const handlePresetChange = (preset) => {
    setDatePreset(preset);
    const today = new Date();
    let f = today.toISOString().split('T')[0];
    let t = today.toISOString().split('T')[0];

    if (preset === 'yesterday') {
      const y = new Date(today);
      y.setDate(today.getDate() - 1);
      f = y.toISOString().split('T')[0];
      t = f;
    } else if (preset === 'thisWeek') {
      const firstDay = new Date(today);
      const day = today.getDay();
      const diff = today.getDate() - day + (day === 0 ? -6 : 1);
      firstDay.setDate(diff);
      f = firstDay.toISOString().split('T')[0];
    } else if (preset === 'thisMonth') {
      f = new Date(today.getFullYear(), today.getMonth(), 1).toISOString().split('T')[0];
    }

    setFromDate(f);
    setToDate(t);
    setPage(1);
  };

  // Fetch Transactions
  const fetchTransactions = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        search: search.trim() || undefined,
        centreId: centreId ? parseInt(centreId) : undefined,
        employeeId: employeeId ? parseInt(employeeId) : undefined,
        paymentMode: paymentMode !== 'all' ? paymentMode : undefined,
        transactionStatus: transactionStatus !== 'all' ? transactionStatus : undefined,
        datePreset,
        fromDate,
        toDate,
        page,
        pageSize,
        sortBy,
        sortDirection
      };

      const res = await settlementService.getDetailedTransactions(params);
      if (res.success && res.data) {
        setTransactions(res.data.items || []);
        setTotalCount(res.data.totalCount || 0);
        setTotalPages(res.data.totalPages || 1);
      } else {
        showToast(res.message || 'Failed to fetch transactions', 'error');
      }
    } catch (err) {
      console.error('Error fetching transactions:', err);
      showToast('Error loading transaction records', 'error');
    } finally {
      setLoading(false);
    }
  }, [
    search,
    centreId,
    employeeId,
    paymentMode,
    transactionStatus,
    datePreset,
    fromDate,
    toDate,
    page,
    pageSize,
    sortBy,
    sortDirection,
    showToast
  ]);

  useEffect(() => {
    fetchTransactions();
  }, [fetchTransactions]);

  // Handle Sort
  const handleSort = (col) => {
    if (sortBy === col) {
      setSortDirection((prev) => (prev === 'ASC' ? 'DESC' : 'ASC'));
    } else {
      setSortBy(col);
      setSortDirection('DESC');
    }
    setPage(1);
  };

  // Helper to clean comma garbage
  const formatInvestigations = (str) => {
    if (!str) return 'Routine Diagnostic Panel';
    const cleaned = str
      .split(',')
      .map((s) => s.trim())
      .filter((s) => s.length > 0)
      .join(', ');
    return cleaned || 'Routine Diagnostic Panel';
  };

  // Instant Filtered Financial Metrics
  const metrics = React.useMemo(() => {
    let totalGross = 0;
    let totalDiscount = 0;
    let totalNet = 0;
    let totalCollections = 0;
    let totalRefunds = 0;

    transactions.forEach((tx) => {
      totalGross += Number(tx.grossAmount || 0);
      totalDiscount += Number(tx.discountAmount || 0);
      totalNet += Number(tx.netAmount || 0);
      if (tx.paidAmount < 0 || tx.transactionStatus === 'Refund') {
        totalRefunds += Math.abs(Number(tx.paidAmount || tx.refundAmount || 0));
      } else {
        totalCollections += Number(tx.paidAmount || 0);
      }
    });

    return {
      count: transactions.length,
      totalCount,
      totalGross,
      totalDiscount,
      totalNet,
      totalCollections,
      totalRefunds,
      netCash: totalCollections - totalRefunds
    };
  }, [transactions, totalCount]);

  // Export to CSV
  const handleExportCSV = () => {
    if (!transactions.length) {
      showToast('No records to export', 'error');
      return;
    }

    const headers = [
      'Transaction Date',
      'Transaction Time',
      'Business Date',
      'Receipt No',
      'Patient ID',
      'Patient Name',
      'Lab No',
      'Order ID',
      'Investigation/Package',
      'Gross Amount',
      'Discount',
      'Net Amount',
      'Paid Amount',
      'Payment Mode',
      'Payment Ref / Txn No',
      'Refund Amount',
      'Refund Status',
      'Settlement Status',
      'Center',
      'Collected By',
      'Created By',
      'Approved By',
      'Transaction Status'
    ];

    const rows = transactions.map((t) => [
      `"${new Date(t.transactionDate).toLocaleDateString()}"`,
      `"${new Date(t.transactionDate).toLocaleTimeString()}"`,
      `"${new Date(t.businessDate).toLocaleDateString()}"`,
      `"${t.receiptNo}"`,
      `"${t.patientId}"`,
      `"${(t.patientName || '').replace(/"/g, '""')}"`,
      `"${t.labNo}"`,
      t.orderId,
      `"${formatInvestigations(t.investigations).replace(/"/g, '""')}"`,
      t.grossAmount.toFixed(2),
      t.discountAmount.toFixed(2),
      t.netAmount.toFixed(2),
      t.paidAmount.toFixed(2),
      `"${t.paymentMode}"`,
      `"${(t.transactionRefNo || '').replace(/"/g, '""')}"`,
      t.refundAmount.toFixed(2),
      `"${t.refundStatus}"`,
      `"${t.settlementStatus}"`,
      `"${(t.centreName || '').replace(/"/g, '""')}"`,
      `"${(t.collectedByName || '').replace(/"/g, '""')}"`,
      `"${(t.createdByName || '').replace(/"/g, '""')}"`,
      `"${(t.approvedByName || '').replace(/"/g, '""')}"`,
      `"${t.transactionStatus}"`
    ]);

    const csvContent =
      'data:text/csv;charset=utf-8,' + [headers.join(','), ...rows.map((e) => e.join(','))].join('\n');
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement('a');
    link.setAttribute('href', encodedUri);
    link.setAttribute('download', `financial_transactions_${fromDate}_to_${toDate}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    showToast('Transactions exported successfully', 'success');
  };

  // Table scrolling & navigation ref
  const tableContainerRef = useRef(null);
  const [scrollInfo, setScrollInfo] = useState({ canScrollLeft: false, canScrollRight: true, scrollPercent: 0 });

  const updateScrollInfo = useCallback(() => {
    const el = tableContainerRef.current;
    if (!el) return;
    const { scrollLeft, scrollWidth, clientWidth } = el;
    const maxScroll = Math.max(0, scrollWidth - clientWidth);
    setScrollInfo({
      canScrollLeft: scrollLeft > 10,
      canScrollRight: scrollLeft < maxScroll - 10,
      scrollPercent: maxScroll > 0 ? (scrollLeft / maxScroll) * 100 : 0
    });
  }, []);

  useEffect(() => {
    const el = tableContainerRef.current;
    if (el) {
      el.addEventListener('scroll', updateScrollInfo, { passive: true });
      window.addEventListener('resize', updateScrollInfo);
      updateScrollInfo();
      return () => {
        el.removeEventListener('scroll', updateScrollInfo);
        window.removeEventListener('resize', updateScrollInfo);
      };
    }
  }, [updateScrollInfo, transactions]);

  const handleScrollNudge = (delta) => {
    if (tableContainerRef.current) {
      tableContainerRef.current.scrollBy({ left: delta, behavior: 'smooth' });
    }
  };

  const handleScrollTo = (leftPos) => {
    if (tableContainerRef.current) {
      tableContainerRef.current.scrollTo({ left: leftPos, behavior: 'smooth' });
    }
  };

  const handleWheelScroll = (e) => {
    if (e.shiftKey && tableContainerRef.current) {
      e.preventDefault();
      tableContainerRef.current.scrollLeft += e.deltaY;
    }
  };

  return (
    <div className="fin-detailed-transactions">
      {/* =========================================================
          COMMAND BAR: 2-TIER SYMMETRICAL FILTER TOOLBAR
          ========================================================= */}
      <div className="fin-cmd-toolbar">
        {/* Tier 1: Search & Date Range */}
        <div className="fin-cmd-row">
          <div className="fin-cmd-unit search-unit">
            <span className="fin-cmd-label">Quick Search</span>
            <div className="fin-cmd-input-box">
              <span className="fin-cmd-icon">🔍</span>
              <input
                type="text"
                placeholder="Search Receipt #, Patient Name, UHID, Lab Work Order..."
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                  setPage(1);
                }}
                className="fin-cmd-input"
              />
              {search && (
                <button
                  type="button"
                  className="fin-cmd-clear"
                  onClick={() => {
                    setSearch('');
                    setPage(1);
                  }}
                  title="Clear search"
                >
                  ✕
                </button>
              )}
            </div>
          </div>

          <div className="fin-cmd-unit">
            <span className="fin-cmd-label">Quick Date Presets</span>
            <div className="fin-preset-group">
              {[
                { id: 'today', label: 'Today' },
                { id: 'yesterday', label: 'Yesterday' },
                { id: 'thisWeek', label: 'This Week' },
                { id: 'thisMonth', label: 'This Month' }
              ].map((p) => (
                <button
                  key={p.id}
                  type="button"
                  className={`fin-preset-pill ${datePreset === p.id ? 'active' : ''}`}
                  onClick={() => handlePresetChange(p.id)}
                >
                  {p.label}
                </button>
              ))}
            </div>
          </div>

          <div className="fin-cmd-unit">
            <span className="fin-cmd-label">Transaction Date Range</span>
            <div className="fin-cmd-range">
              <input
                type="date"
                value={fromDate}
                onChange={(e) => {
                  setFromDate(e.target.value);
                  setDatePreset('custom');
                  setPage(1);
                }}
                className="fin-cmd-date"
              />
              <span className="range-sep">→</span>
              <input
                type="date"
                value={toDate}
                onChange={(e) => {
                  setToDate(e.target.value);
                  setDatePreset('custom');
                  setPage(1);
                }}
                className="fin-cmd-date"
              />
            </div>
          </div>
        </div>

        {/* Tier 2: Filtering Dimensions & Action Buttons */}
        <div className="fin-cmd-row" style={{ marginTop: '12px' }}>
          {isAccountsOrAdmin && (
            <div className="fin-cmd-unit">
              <span className="fin-cmd-label">Center</span>
              <select
                value={centreId}
                onChange={(e) => {
                  setCentreId(e.target.value);
                  setPage(1);
                }}
                className="fin-cmd-select"
              >
                <option value="">All Diagnostic Centers</option>
                {centres.map((c) => (
                  <option key={c.centreId || c.centreId} value={c.centreId || c.centreId}>
                    {c.centreName}
                  </option>
                ))}
              </select>
            </div>
          )}

          {isAccountsOrAdmin && (
            <div className="fin-cmd-unit">
              <span className="fin-cmd-label">Staff / Receptionist</span>
              <select
                value={employeeId}
                onChange={(e) => {
                  setEmployeeId(e.target.value);
                  setPage(1);
                }}
                className="fin-cmd-select"
              >
                <option value="">All Staff (Consolidated)</option>
                {receptionists.map((r) => (
                  <option key={r.employeeId} value={r.employeeId}>
                    {r.employeeName} ({r.centreName})
                  </option>
                ))}
              </select>
            </div>
          )}

          <div className="fin-cmd-unit">
            <span className="fin-cmd-label">Payment Channel</span>
            <select
              value={paymentMode}
              onChange={(e) => {
                setPaymentMode(e.target.value);
                setPage(1);
              }}
              className="fin-cmd-select"
            >
              <option value="all">All Payment Channels</option>
              <option value="Cash">💵 Cash</option>
              <option value="UPI">📱 UPI / QR</option>
              <option value="Credit Card">💳 Credit Card</option>
              <option value="Debit Card">💳 Debit Card</option>
              <option value="Bank Transfer">🏦 Bank Transfer</option>
              <option value="Other">📑 Other Modes</option>
            </select>
          </div>

          <div className="fin-cmd-unit">
            <span className="fin-cmd-label">Record Type</span>
            <select
              value={transactionStatus}
              onChange={(e) => {
                setTransactionStatus(e.target.value);
                setPage(1);
              }}
              className="fin-cmd-select"
              style={{ minWidth: '130px' }}
            >
              <option value="all">All Records</option>
              <option value="collection">Collections Only</option>
              <option value="refund">Refunds Only</option>
            </select>
          </div>

          <div className="fin-cmd-actions">
            <button
              type="button"
              className="fin-btn-ghost"
              onClick={fetchTransactions}
              title="Refresh transaction records"
            >
              🔄 Refresh
            </button>
            <button
              type="button"
              className="fin-btn-brand"
              onClick={handleExportCSV}
              title="Export grid data to CSV file"
            >
              📥 Export CSV
            </button>
          </div>
        </div>
      </div>

      {/* =========================================================
          DATA GRID CARD WRAPPER (22 REQUIRED AUDIT COLUMNS)
          ========================================================= */}
      <div className="fin-grid-card">
        {/* Instant Financial Metrics Ribbon */}
        <div className="fin-grid-metric-strip">
          <div className="metric-chip">
            <span className="metric-chip-lbl">Filtered Transactions</span>
            <span className="metric-chip-val font-bold">{totalCount} Records</span>
          </div>
          <div className="metric-chip-sep" />
          <div className="metric-chip">
            <span className="metric-chip-lbl">Total Gross Billed</span>
            <span className="metric-chip-val">₹{metrics.totalGross.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
          </div>
          <div className="metric-chip-sep" />
          <div className="metric-chip">
            <span className="metric-chip-lbl">Total Collections</span>
            <span className="metric-chip-val text-emerald font-bold">+₹{metrics.totalCollections.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
          </div>
          <div className="metric-chip-sep" />
          <div className="metric-chip">
            <span className="metric-chip-lbl">Total Refunds</span>
            <span className="metric-chip-val text-rose font-bold">
              {metrics.totalRefunds > 0 ? `-₹${metrics.totalRefunds.toLocaleString('en-IN', { minimumFractionDigits: 2 })}` : '₹0.00'}
            </span>
          </div>
          <div className="metric-chip-sep" />
          <div className="metric-chip">
            <span className="metric-chip-lbl">Net Cashflow</span>
            <span className="metric-chip-val text-blue font-bold">₹{metrics.netCash.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
          </div>
        </div>

        {/* Card Header Strip with Horizontal Scroll Navigation */}
        <div className="fin-grid-card-head">
          <div className="head-info">
            <span className="head-title">📑 Master Transaction & Settlement Ledger</span>
            <span className="head-meta">
              Showing <strong>{transactions.length}</strong> of <strong>{totalCount}</strong> transactions
              {search && <span> matching <em>"{search}"</em></span>}
            </span>
          </div>

          {/* Quick Horizontal Scroll Jump Bar & Nudge Controls */}
          <div className="head-scroll-nav">
            <span className="scroll-nav-label">Scroll Columns:</span>
            <div className="scroll-nav-jumps">
              <button
                type="button"
                className="fin-nav-pill"
                onClick={() => handleScrollTo(0)}
                title="Scroll to Date, Receipt & Patient details"
              >
                🏷️ Details
              </button>
              <button
                type="button"
                className="fin-nav-pill"
                onClick={() => handleScrollTo(300)}
                title="Scroll to Lab, Orders & Investigations"
              >
                🧪 Tests
              </button>
              <button
                type="button"
                className="fin-nav-pill"
                onClick={() => handleScrollTo(600)}
                title="Scroll to Gross, Discount & Net Amount"
              >
                💰 Billing
              </button>
              <button
                type="button"
                className="fin-nav-pill"
                onClick={() => handleScrollTo(950)}
                title="Scroll to Paid Amount, Payment Mode & References"
              >
                💳 Payment
              </button>
              <button
                type="button"
                className="fin-nav-pill"
                onClick={() => handleScrollTo(1400)}
                title="Scroll to Diagnostic Center, Staff & Audit Trail"
              >
                🏥 Audit
              </button>
            </div>

            <div className="scroll-arrow-group">
              <button
                type="button"
                className="fin-nav-arrow"
                disabled={!scrollInfo.canScrollLeft}
                onClick={() => handleScrollNudge(-350)}
                title="Scroll Left (350px)"
              >
                ◀ Left
              </button>
              <button
                type="button"
                className="fin-nav-arrow"
                disabled={!scrollInfo.canScrollRight}
                onClick={() => handleScrollNudge(350)}
                title="Scroll Right (350px)"
              >
                Right ▶
              </button>
            </div>
          </div>

          <div className="head-controls">
            <label className="rows-label">Rows per page:</label>
            <select
              value={pageSize}
              onChange={(e) => {
                setPageSize(parseInt(e.target.value));
                setPage(1);
              }}
              className="fin-rows-select"
            >
              <option value="10">10</option>
              <option value="25">25</option>
              <option value="50">50</option>
              <option value="100">100</option>
            </select>
          </div>
        </div>

        {/* Interactive Top Horizontal Scrollbar Progress Track */}
        <div
          className="fin-top-scrollbar-track"
          onClick={(e) => {
            const rect = e.currentTarget.getBoundingClientRect();
            const clickX = e.clientX - rect.left;
            const ratio = Math.max(0, Math.min(1, clickX / rect.width));
            if (tableContainerRef.current) {
              const maxScroll = tableContainerRef.current.scrollWidth - tableContainerRef.current.clientWidth;
              tableContainerRef.current.scrollTo({ left: ratio * maxScroll, behavior: 'smooth' });
            }
          }}
          title="Top scrollbar: Click anywhere on track to jump across columns"
        >
          <div
            className="fin-top-scrollbar-thumb"
            style={{
              left: `${scrollInfo.scrollPercent}%`,
              transform: `translateX(-${scrollInfo.scrollPercent}%)`
            }}
          />
        </div>

        {/* Scrollable Table Viewport with Sticky Pinned Identifier Columns */}
        <div
          className="fin-table-viewport"
          ref={tableContainerRef}
          onWheel={handleWheelScroll}
        >
          <table className="fin-data-grid">
            <thead>
              <tr>
                {/* 1. Date & Time (STICKY 1) */}
                <th
                  onClick={() => handleSort('Date')}
                  className="sortable text-left fin-col-sticky-1"
                  style={{ minWidth: '120px' }}
                >
                  Date & Time {sortBy === 'Date' ? (sortDirection === 'ASC' ? '▲' : '▼') : ''}
                </th>

                {/* 2. Receipt # (STICKY 2) */}
                <th
                  onClick={() => handleSort('ReceiptNo')}
                  className="sortable text-left fin-col-sticky-2"
                  style={{ minWidth: '190px' }}
                >
                  Receipt # {sortBy === 'ReceiptNo' ? (sortDirection === 'ASC' ? '▲' : '▼') : ''}
                </th>

                {/* 3. Patient Details (STICKY 3 - WITH SHADOW) */}
                <th
                  className="fin-col-sticky-3"
                  style={{ minWidth: '180px' }}
                >
                  Patient Details
                </th>

                {/* 4. Business Date */}
                <th style={{ minWidth: '105px' }}>Business Date</th>

                {/* 5. Lab & Order # */}
                <th style={{ minWidth: '155px' }}>Lab & Order #</th>

                {/* 6. Investigation / Package */}
                <th style={{ minWidth: '220px' }}>Investigation / Package</th>

                {/* 7. Gross */}
                <th style={{ minWidth: '95px' }} className="text-right">Gross (₹)</th>

                {/* 8. Discount */}
                <th style={{ minWidth: '90px' }} className="text-right">Discount</th>

                {/* 9. Net */}
                <th style={{ minWidth: '100px' }} className="text-right">Net (₹)</th>

                {/* 10. Paid Amount */}
                <th style={{ minWidth: '110px' }} className="text-right">Paid Amount</th>

                {/* 11. Payment Mode */}
                <th style={{ minWidth: '110px' }}>Payment Mode</th>

                {/* 12. Reference # */}
                <th style={{ minWidth: '120px' }}>Reference #</th>

                {/* 13. Refund Amount */}
                <th style={{ minWidth: '95px' }} className="text-right">Refund (₹)</th>

                {/* 14. Refund Status */}
                <th style={{ minWidth: '105px' }}>Refund Status</th>

                {/* 15. Settlement */}
                <th style={{ minWidth: '110px' }}>Settlement</th>

                {/* 16. Diagnostic Center */}
                <th style={{ minWidth: '140px' }}>Diagnostic Center</th>

                {/* 17. Collected By */}
                <th style={{ minWidth: '130px' }}>Collected By</th>

                {/* 18. Created By */}
                <th style={{ minWidth: '110px' }}>Created By</th>

                {/* 19. Approved By */}
                <th style={{ minWidth: '100px' }}>Approved By</th>

                {/* 20. Type */}
                <th style={{ minWidth: '90px' }}>Type</th>

                {/* 21. Action */}
                <th style={{ minWidth: '85px' }} className="text-center">Action</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="21" className="fin-table-empty">
                    <div className="fin-loading-spinner" />
                    <div>Loading detailed transaction records...</div>
                  </td>
                </tr>
              ) : transactions.length === 0 ? (
                <tr>
                  <td colSpan="21" className="fin-table-empty">
                    No transactions found matching your search and filter criteria.
                  </td>
                </tr>
              ) : (
                transactions.map((tx) => {
                  const isRefund = tx.transactionStatus === 'Refund' || tx.paidAmount < 0;
                  return (
                    <tr key={tx.receiptId} className={isRefund ? 'row-refund' : ''}>
                      {/* 1. Date & Time (STICKY 1) */}
                      <td className="fin-col-sticky-1">
                        <div className="cell-primary font-bold">
                          {new Date(tx.transactionDate).toLocaleDateString('en-IN', {
                            day: '2-digit',
                            month: 'short',
                            year: 'numeric'
                          })}
                        </div>
                        <div className="cell-muted">
                          {new Date(tx.transactionDate).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                        </div>
                      </td>

                      {/* 2. Receipt No (STICKY 2) */}
                      <td className="fin-col-sticky-2">
                        <button
                          type="button"
                          className="fin-receipt-btn"
                          onClick={() => onOpenReceipt && onOpenReceipt(tx.receiptNo)}
                          title="Click to view & print official receipt voucher"
                        >
                          <span className="receipt-icon">📄</span>
                          <span className="receipt-no-text">{tx.receiptNo}</span>
                        </button>
                        {tx.refundAgainstReceiptNo && (
                          <div className="fin-refund-ref-tag" title="Refund against original receipt">
                            ↩ Against: {tx.refundAgainstReceiptNo}
                          </div>
                        )}
                      </td>

                      {/* 3. Patient Details (STICKY 3) */}
                      <td className="fin-col-sticky-3">
                        <div className="cell-primary font-bold">{tx.patientName}</div>
                        <div className="cell-mono-uhid">UHID: {tx.patientId}</div>
                      </td>

                      {/* 4. Business Date */}
                      <td>
                        <span className="fin-tag-muted">
                          {new Date(tx.businessDate).toLocaleDateString('en-IN', {
                            day: '2-digit',
                            month: 'short',
                            year: 'numeric'
                          })}
                        </span>
                      </td>

                      {/* 5. Lab & Order Details */}
                      <td>
                        <div className="cell-lab-mono">{tx.labNo}</div>
                        <div className="cell-order-tag">Order #{tx.orderId}</div>
                      </td>

                      {/* 6. Investigation / Package */}
                      <td className="cell-investigation" title={tx.investigations}>
                        <span className="fin-inv-pill">
                          🧪 {formatInvestigations(tx.investigations)}
                        </span>
                      </td>

                      {/* 7. Gross Amount */}
                      <td className="text-right text-slate font-medium">
                        ₹{tx.grossAmount.toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                      </td>

                      {/* 8. Discount Amount */}
                      <td className="text-right">
                        {tx.discountAmount > 0 ? (
                          <span className="fin-disc-tag">
                            -₹{tx.discountAmount.toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                          </span>
                        ) : (
                          <span className="text-slate">—</span>
                        )}
                      </td>

                      {/* 9. Net Amount */}
                      <td className="text-right font-bold text-slate-dark">
                        ₹{tx.netAmount.toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                      </td>

                      {/* 10. Paid Amount */}
                      <td className="text-right">
                        <span className={`fin-paid-badge ${isRefund ? 'refund' : 'collection'}`}>
                          {isRefund ? '-' : '+'}₹{Math.abs(tx.paidAmount).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                        </span>
                      </td>

                      {/* 11. Payment Mode */}
                      <td>
                        <span className={`fin-mode-tag ${tx.paymentMode.toLowerCase().replace(/\s+/g, '')}`}>
                          {tx.paymentMode === 'Cash' ? '💵' : tx.paymentMode === 'UPI' ? '📱' : tx.paymentMode.includes('Card') ? '💳' : '🏦'} {tx.paymentMode}
                        </span>
                      </td>

                      {/* 12. Transaction Ref / Txn No */}
                      <td>
                        <span className="cell-muted" title={tx.transactionRefNo || ''}>
                          {tx.transactionRefNo || '—'}
                        </span>
                      </td>

                      {/* 13. Refund Amount */}
                      <td className="text-right">
                        {tx.refundAmount > 0 ? (
                          <span className="font-bold text-rose">
                            ₹{tx.refundAmount.toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                          </span>
                        ) : (
                          <span className="text-slate">—</span>
                        )}
                      </td>

                      {/* 14. Refund Status */}
                      <td>
                        {tx.refundStatus && tx.refundStatus !== 'None' ? (
                          <span className={`fin-status-pill ${tx.refundStatus.toLowerCase()}`}>
                            {tx.refundStatus}
                          </span>
                        ) : (
                          <span className="text-slate" style={{ fontSize: '11px' }}>—</span>
                        )}
                      </td>

                      {/* 15. Settlement Status */}
                      <td>
                        <span className={`fin-status-pill ${tx.settlementStatus === 'Advance' ? 'advance' : 'reconciled'}`}>
                          {tx.settlementStatus || 'Settled'}
                        </span>
                      </td>

                      {/* 16. Center */}
                      <td>
                        <span className="cell-primary font-medium">{tx.centreName}</span>
                      </td>

                      {/* 17. Collected By */}
                      <td>
                        <div className="cell-primary font-medium">{tx.collectedByName}</div>
                      </td>

                      {/* 18. Created By */}
                      <td>
                        <div className="cell-muted">{tx.createdByName || tx.collectedByName}</div>
                      </td>

                      {/* 19. Approved By */}
                      <td>
                        <div className="cell-muted">{tx.approvedByName || '—'}</div>
                      </td>

                      {/* 20. Status (Collection vs Refund) */}
                      <td>
                        <span className={`fin-status-pill ${isRefund ? 'difference' : 'reconciled'}`}>
                          {tx.transactionStatus}
                        </span>
                      </td>

                      {/* 21. Actions */}
                      <td className="text-center">
                        <button
                          type="button"
                          className="fin-btn-row-receipt"
                          onClick={() => onOpenReceipt && onOpenReceipt(tx.receiptNo)}
                          title="Print receipt voucher"
                        >
                          📄 Receipt
                        </button>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {/* Card Footer: Pagination Controls */}
        <div className="fin-grid-card-foot">
          <div className="foot-info">
            Showing Page <strong>{page}</strong> of <strong>{totalPages}</strong> ({totalCount} total transactions)
          </div>
          <div className="foot-actions">
            <button
              type="button"
              className="fin-page-btn"
              disabled={page <= 1 || loading}
              onClick={() => setPage((prev) => Math.max(1, prev - 1))}
            >
              ◀ Previous
            </button>
            {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
              let pNum = page - 2 + i;
              if (pNum < 1) pNum = i + 1;
              if (pNum > totalPages) return null;
              return (
                <button
                  key={pNum}
                  type="button"
                  className={`fin-page-pill ${page === pNum ? 'active' : ''}`}
                  onClick={() => setPage(pNum)}
                >
                  {pNum}
                </button>
              );
            })}
            <button
              type="button"
              className="fin-page-btn"
              disabled={page >= totalPages || loading}
              onClick={() => setPage((prev) => Math.min(totalPages, prev + 1))}
            >
              Next ▶
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DetailedTransactionsTab;
